import React, { useEffect, useMemo, useState } from 'react'
import {
  IconHome,
  IconChat,
  IconMail,
  IconMeetingType,
  IconWorkingHours,
  IconPersonalPage,
  IconIntegration,
  IconArrowLeftBarLeft,
  IconChevronRight,
  IconGlobe,
  IconReports,
} from '@chilipiper/icons/figma-generated/src/icon'
import { rolloutFlags, useRolloutFlagsVersion } from '@chilipiper/rollout'
import { IconChiliPiper24 } from '@chilipiper/icons/figma-generated/src/logo'
import { Box, css, Divider, Flex, styled } from '@chilipiper/design-system'
import { Popover, UnstyledButton, Icon } from '@chilipiper/design-system/src/new'
import { usePersonalDetails } from '@chilipiper/workspace-queries/src/api-queries/personal-details'
import { useSetUserAvailability, useGetUserAvailability } from '@chilipiper/scheduling-shared'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { NavLink } from '../../types'
import { useGlobalState } from '../../hooks/context'
import { meetingTypesRoutes, routes } from '../../routes/routes'
import { Storage } from '../../utils/storage'
import { CurrentUserAvatar } from '../CurrentUserAvatar'
import { LinkNavItem } from './LinkNavItem'
import { PersonalSettingsMenu } from './personal-settings-menu/PersonalSettingsMenu'
import { AdminCenterLinkWithLicenseRequest } from './AdminCenterLinkWithLicenseRequest'
import { NavItem } from './NavItem'
import { cdpNavLinks } from './cdpNavLinks'
import { SupportSection } from './SupportSection'
import { useHasReviewableDrafts } from '@/hooks/useHasReviewableDrafts'
import { useShouldShowOnlineToggle } from '@/hooks/useShouldShowOnlineToggle'
import { useUserHasLiveChatLicense } from '@/hooks/useUserHasLiveChatLicense'
import { useTrackFeature } from '@/hooks/useTracking'
import { ChilicalEvent } from '@/tracking/events'
export const LeftNav = () => {
  const { tenantId, userId, user, isTiered } = useFireSessionContext()
  const { data: personalDetails } = usePersonalDetails(userId)
  const userHasLiveChatLicense = useUserHasLiveChatLicense()
  const hasReviewableDrafts = useHasReviewableDrafts()
  const shouldShowOnlineToggle = useShouldShowOnlineToggle()
  // Flag reads are plain reads of a mutable object. The tenant-targeted resolution lands after the
  // initial one (and after this mounts, on a browser with no cached identity), so re-render on each
  // resolution and feed the links memo booleans; otherwise a flagged entry stays hidden until the
  // next reload.
  useRolloutFlagsVersion()
  const isMeetingReportsEnabled = rolloutFlags.enableChilicalMeetingsReports?.isEnabled() ?? false
  const isGtmoGalaxyEnabled = rolloutFlags.gtmoGalaxy?.isEnabled() ?? false
  const isPublicCdpEnabled = rolloutFlags.publicCdp?.isEnabled() ?? false
  const isInternalCdpEnabled = rolloutFlags.internalCdp?.isEnabled() ?? false

  const [isPersonalMenuOpen, setPersonalMenuOpen] = useState(false)
  const { mutateAsync: setUserAvailability } = useSetUserAvailability(tenantId, userId)
  const { data: userAvailability } = useGetUserAvailability(tenantId, userId)

  const { globalState, setGlobalState } = useGlobalState()
  const track = useTrackFeature()

  const { isNavExpanded } = globalState

  const name = `${personalDetails?.firstName ?? ''} ${personalDetails?.lastName ?? ''}`
  const trimmedName = name.trim()
  const displayName = trimmedName || user.email || ''

  const links = useMemo<NavLink[]>(() => {
    return [
      {
        label: 'Home',
        icon: IconHome,
        path: routes.home.index,
        chmln: 'left-nav-home',
        feature: 'calendar',
      },
      // The route itself stays mounted; only the entry point is flagged, so a deep link still works.
      isMeetingReportsEnabled
        ? {
            label: 'Meeting Reports',
            icon: IconReports,
            path: routes.meetingReports.index,
            dataId: 'left-nav-meeting-reports',
            feature: 'meeting_reports',
          }
        : undefined,
      !userHasLiveChatLicense && !isTiered
        ? undefined
        : {
            label: 'Chat Inbox',
            icon: IconChat,
            path: routes.inbox,
            dataId: 'left-nav-inbox',
            isLocked: !userHasLiveChatLicense,
            feature: 'chat',
          },
      hasReviewableDrafts
        ? {
            label: 'Email Review',
            icon: IconMail,
            path: routes.emailAgentReview,
            dataId: 'left-nav-email-review',
            feature: 'email_composer',
          }
        : undefined,
      {
        label: 'Meeting Types',
        icon: IconMeetingType,
        path: meetingTypesRoutes.meetingTypes(),
        dataId: 'left-nav-meeting-types',
        feature: 'meeting_types',
      },
      {
        label: 'Availability',
        icon: IconWorkingHours,
        path: routes.availability.index,
        dataId: 'left-nav-availability',
        feature: 'availability',
      },
      {
        label: 'Personal Page',
        icon: IconPersonalPage,
        path: routes.personalPage,
        dataId: 'left-nav-personal-page',
        feature: 'personal_page',
      },
      {
        label: 'Integrations',
        icon: IconIntegration,
        path: routes.integrations,
        dataId: 'left-nav-integrations',
        feature: 'integrations',
      },
      isGtmoGalaxyEnabled
        ? {
            label: 'CDP',
            icon: IconGlobe,
            path: routes.gtmo.board,
            dataId: 'left-nav-gtmo',
          }
        : undefined,
      ...cdpNavLinks({ isPublicCdpEnabled, isInternalCdpEnabled }),
    ].filter(Boolean) as NavLink[]
  }, [
    userHasLiveChatLicense,
    isTiered,
    hasReviewableDrafts,
    isMeetingReportsEnabled,
    isGtmoGalaxyEnabled,
    isPublicCdpEnabled,
    isInternalCdpEnabled,
  ])

  useEffect(() => {
    Storage.set('LEFTNAV_STATE', isNavExpanded ? 'expanded' : 'collapsed')
  }, [isNavExpanded])

  const isUserAway =
    userAvailability?.type === 'LastConfigured' &&
    userAvailability?.toggle.status.type !== 'Available'

  const userStatus = shouldShowOnlineToggle ? (isUserAway ? 'offline' : 'online') : undefined
  return (
    <StyledNavContainer
      data-test-id='LeftNav'
      isExpanded={isNavExpanded}
      bg='bg/section'
      boxShadow='border/cell'
      role='navigation'
    >
      <Icon icon={IconChiliPiper24} color='fg/brand' ml={5} mt={6} mb='30px' w={6} h={6} />
      <Box p={3} as='ul' flex={1}>
        {links.map(link => {
          return (
            <Box as='li' key={link.label} w='100%' data-id={link.dataId}>
              <LinkNavItem
                isExpanded={isNavExpanded}
                link={link}
                onClick={() => {
                  if (link.feature) {
                    track(ChilicalEvent.NavItemClicked, link.feature)
                  }
                }}
              />
            </Box>
          )
        })}
      </Box>

      <AdminCenterLinkWithLicenseRequest isExpanded={isNavExpanded} />
      <Divider />

      <Flex flexDirection='column' gap={2} p={3}>
        <Popover
          placement='top start'
          offset={-8}
          crossOffset={112}
          data-test-id='dropdown'
          onOpenChange={isMenuVisible => setPersonalMenuOpen(isMenuVisible)}
          isOpen={isPersonalMenuOpen}
          trigger={triggerProps => (
            <UnstyledButton ariaProps={triggerProps} aria-label='Open user menu'>
              <NavItem
                isExpanded={isNavExpanded}
                icon={<CurrentUserAvatar status={userStatus} name={displayName} />}
                label={displayName}
                sublabel={trimmedName ? user.email : undefined}
                rightIcon={IconChevronRight}
              />
            </UnstyledButton>
          )}
        >
          <PersonalSettingsMenu
            isUserAway={isUserAway}
            onToggleAwayMode={() => {
              setUserAvailability({
                status: {
                  type: isUserAway ? 'Available' : 'Away',
                },
              })
            }}
            onMenuItemClick={() => setPersonalMenuOpen(false)}
          />
        </Popover>

        <SupportSection isExpanded={isNavExpanded} />

        <UnstyledButton
          onClick={() => {
            setGlobalState({ isNavExpanded: !isNavExpanded })
          }}
        >
          <NavItem
            isExpanded={isNavExpanded}
            icon={
              <StyledIcon
                icon={IconArrowLeftBarLeft}
                $isExpanded={isNavExpanded}
                color='fg/moderate'
              />
            }
            label='Collapse'
          />
        </UnstyledButton>
      </Flex>
    </StyledNavContainer>
  )
}

const StyledIcon = styled(Icon)<{ $isExpanded?: boolean }>`
  transform: rotateY(${({ $isExpanded }) => ($isExpanded ? 0 : 180)}deg);
  transition: transform 0.2s;
`

const NAVIGATION_WIDTH = '64px'
const EXPANDED_NAVIGATION_WIDTH = '296px'

const StyledNavContainer = styled(Flex)<{ isExpanded?: boolean }>`
  flex-direction: column;
  height: 100%;
  transition: 0.3s;
  width: ${NAVIGATION_WIDTH};

  ${({ isExpanded }) =>
    isExpanded &&
    css`
      width: ${EXPANDED_NAVIGATION_WIDTH};
      background: ${({ theme }) => theme.colors['bg/section']};
    `}
`
